import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Typography, Box, Link } from '@mui/material';
import Header from '@/components/layout/Header';
import { useI18n } from '@/hooks/useI18n';

const PrivacyPolicyPage = () => {
    const navigate = useNavigate();
    const { t } = useI18n();

    return (
        <>
            <Header title={t('pages.privacy.title')} onBack={() => navigate('/')} />
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
                    Datenschutzerklärung
                </Typography>

                {/* 1. Verantwortlicher */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
                        1. Verantwortlicher
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Verantwortlich für die Datenverarbeitung auf dieser Website ist:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {t('pages.imprint.companyName')}<br />
                        {t('pages.imprint.address')}<br />
                        {t('pages.imprint.cityPostal')}<br />
                        {t('pages.imprint.email')}<br />
                        {t('pages.imprint.phone')}
                    </Typography>
                </Box>

                {/* 2. Allgemeine Hinweise */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
                        2. Allgemeine Hinweise zur Datenverarbeitung
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Wir nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre personenbezogenen Daten vertraulich und entsprechend der gesetzlichen Datenschutzvorschriften sowie dieser Datenschutzerklärung.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Die Nutzung unserer Website ist in der Regel ohne Angabe personenbezogener Daten möglich. Soweit auf unseren Seiten personenbezogene Daten (beispielsweise Name, Anschrift oder E-Mail-Adressen) erhoben werden, erfolgt dies, soweit möglich, stets auf freiwilliger Basis. Diese Daten werden ohne Ihre ausdrückliche Zustimmung nicht an Dritte weitergegeben.
                    </Typography>
                </Box>

                {/* 3. Erhebung und Speicherung personenbezogener Daten */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
                        3. Erhebung und Speicherung personenbezogener Daten
                    </Typography>
                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                        3.1 Art und Zweck der Datenverarbeitung
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Bei der Nutzung unserer Anwendung für Reiseverwaltung verarbeiten wir folgende personenbezogene Daten:
                    </Typography>
                    <Typography component="div" variant="body1" sx={{ mb: 2 }}>
                        <ul>
                            <li>Name und Vorname</li>
                            <li>E-Mail-Adresse</li>
                            <li>Geburtsdatum</li>
                            <li>Mitgliedsstatus (Ski-Club)</li>
                            <li>Unterkunftspräferenzen</li>
                            <li>Skipass-Auswahl</li>
                            <li>Transportoptionen</li>
                            <li>Zahlungsinformationen (für Abrechnungszwecke)</li>
                        </ul>
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Die Erhebung dieser Daten erfolgt, um Ihre Reiseanmeldung zu verwalten, die Organisation der Reise zu koordinieren und Abrechnungen durchzuführen.
                    </Typography>
                </Box>

                {/* 4. Google Firebase / Firestore */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
                        4. Nutzung von Google Firebase / Firestore
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Diese Anwendung nutzt Google Firebase und Google Cloud Firestore für die Speicherung und Verwaltung von Daten. Firebase ist ein Dienst der Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland („Google").
                    </Typography>
                    
                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                        4.1 Firebase Authentication
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Wir verwenden Firebase Authentication zur Benutzerauthentifizierung. Dabei werden folgende Daten verarbeitet:
                    </Typography>
                    <Typography component="div" variant="body1" sx={{ mb: 2 }}>
                        <ul>
                            <li>E-Mail-Adresse</li>
                            <li>Verschlüsseltes Passwort</li>
                            <li>Anmeldezeitpunkte</li>
                            <li>Bei Google-Anmeldung: Google-Profil-ID und Name</li>
                        </ul>
                    </Typography>

                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                        4.2 Cloud Firestore Datenbank
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Alle Anwendungsdaten werden in Google Cloud Firestore gespeichert. Die Datenverarbeitung erfolgt im Auftrag und gemäß unseren Weisungen. Google handelt als Auftragsverarbeiter nach Art. 28 DSGVO.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Die Daten werden in Rechenzentren innerhalb der Europäischen Union gespeichert. Eine Übermittlung in Drittländer findet nur unter Beachtung der EU-Standardvertragsklauseln statt.
                    </Typography>

                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                        4.3 Firebase Cloud Functions
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Wir nutzen Firebase Cloud Functions zur serverseitigen Verarbeitung von Daten und zur Ausführung von Backend-Logik. Cloud Functions sind ereignisgesteuerte Funktionen, die automatisch ausgeführt werden, wenn bestimmte Ereignisse in der Anwendung eintreten.
                    </Typography>
                    {/* <Typography variant="body1" sx={{ mb: 2 }}>
                        Folgende Verarbeitungen werden durch Cloud Functions durchgeführt:
                    </Typography>
                    <Typography component="div" variant="body1" sx={{ mb: 2 }}>
                        <ul>
                            <li>Validierung und Verarbeitung von Benutzeranmeldungen</li>
                            <li>Automatisierte E-Mail-Benachrichtigungen</li>
                            <li>Datenvalidierung und -transformationen</li>
                            <li>Berechnung von Abrechnungen und Zahlungen</li>
                            <li>Synchronisation von Daten zwischen verschiedenen Diensten</li>
                        </ul>
                    </Typography> */}
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Die Cloud Functions werden in Rechenzentren innerhalb der Europäischen Union ausgeführt. Die Verarbeitung erfolgt ausschließlich nach unseren Weisungen und dient der Erfüllung unserer vertraglichen Verpflichtungen Ihnen gegenüber.
                    </Typography>

                    {/* <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                        4.3 Firebase Hosting
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Die Website wird über Firebase Hosting bereitgestellt. Dabei können folgende Daten erfasst werden:
                    </Typography>
                    <Typography component="div" variant="body1" sx={{ mb: 2 }}>
                        <ul>
                            <li>IP-Adresse</li>
                            <li>Datum und Uhrzeit des Zugriffs</li>
                            <li>Browser-Typ und -Version</li>
                            <li>Betriebssystem</li>
                        </ul>
                    </Typography> */}

                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                        4.4 Rechtsgrundlage
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung) sowie Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an der sicheren und zuverlässigen Bereitstellung unserer Dienste).
                    </Typography>

                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Weitere Informationen zum Datenschutz bei Google Firebase finden Sie unter:{' '}
                        <Link href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">
                            https://firebase.google.com/support/privacy
                        </Link>
                    </Typography>
                </Box>

                {/* 5. Weitergabe von Daten */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
                        5. Weitergabe von Daten
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Eine Übermittlung Ihrer persönlichen Daten an Dritte zu anderen als den im Folgenden aufgeführten Zwecken findet nicht statt. Wir geben Ihre persönlichen Daten nur an Dritte weiter, wenn:
                    </Typography>
                    <Typography component="div" variant="body1" sx={{ mb: 2 }}>
                        <ul>
                            <li>Sie Ihre nach Art. 6 Abs. 1 S. 1 lit. a DSGVO ausdrückliche Einwilligung dazu erteilt haben,</li>
                            <li>die Weitergabe nach Art. 6 Abs. 1 S. 1 lit. f DSGVO zur Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen erforderlich ist und kein Grund zur Annahme besteht, dass Sie ein überwiegendes schutzwürdiges Interesse an der Nichtweitergabe Ihrer Daten haben,</li>
                            <li>für den Fall, dass für die Weitergabe nach Art. 6 Abs. 1 S. 1 lit. c DSGVO eine gesetzliche Verpflichtung besteht, sowie</li>
                            <li>dies gesetzlich zulässig und nach Art. 6 Abs. 1 S. 1 lit. b DSGVO für die Abwicklung von Vertragsverhältnissen mit Ihnen erforderlich ist (z.B. Weitergabe an Unterkunftsanbieter oder Transportdienstleister).</li>
                        </ul>
                    </Typography>
                </Box>

                {/* 6. Speicherdauer */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
                        6. Speicherdauer
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Wir speichern personenbezogene Daten nur so lange, wie dies zur Erfüllung der verfolgten Zwecke notwendig ist oder Sie eingewilligt haben. Im Falle eines Widerspruchs gegen die Verarbeitung werden Ihre Daten gelöscht, es sei denn, es greifen gesetzliche Aufbewahrungsfristen (z.B. steuerrechtliche oder handelsrechtliche Aufbewahrungspflichten von in der Regel 6 bis 10 Jahren).
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Reisedaten werden nach Abschluss der Reise und Abrechnung für maximal 2 Jahre aufbewahrt, sofern keine längeren gesetzlichen Aufbewahrungsfristen bestehen.
                    </Typography>
                </Box>

                {/* 7. Ihre Rechte */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
                        7. Ihre Rechte als betroffene Person
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Ihnen stehen grundsätzlich die Rechte auf Auskunft, Berichtigung, Löschung, Einschränkung, Datenübertragbarkeit, Widerruf und Widerspruch zu. Wenn Sie glauben, dass die Verarbeitung Ihrer Daten gegen das Datenschutzrecht verstößt oder Ihre datenschutzrechtlichen Ansprüche sonst in einer Weise verletzt worden sind, können Sie sich bei der Aufsichtsbehörde beschweren.
                    </Typography>

                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                        7.1 Recht auf Auskunft
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Sie haben das Recht, von uns eine Bestätigung darüber zu verlangen, ob Sie betreffende personenbezogene Daten verarbeitet werden. Sofern dies der Fall ist, haben Sie ein Recht auf Auskunft über diese Daten.
                    </Typography>

                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                        7.2 Recht auf Berichtigung
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Sie haben das Recht, von uns die Berichtigung unrichtiger personenbezogener Daten zu verlangen. Ferner steht Ihnen das Recht zu, die Vervollständigung unvollständiger personenbezogener Daten zu verlangen.
                    </Typography>

                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                        7.3 Recht auf Löschung
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Sie haben das Recht, von uns zu verlangen, dass die Sie betreffenden personenbezogenen Daten unverzüglich gelöscht werden, sofern einer der gesetzlichen Gründe zutrifft und soweit die Verarbeitung nicht erforderlich ist.
                    </Typography>

                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                        7.4 Recht auf Einschränkung der Verarbeitung
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Sie haben das Recht, von uns die Einschränkung der Verarbeitung zu verlangen, wenn eine der gesetzlichen Voraussetzungen gegeben ist.
                    </Typography>

                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                        7.5 Recht auf Datenübertragbarkeit
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Sie haben das Recht, die Sie betreffenden personenbezogenen Daten, die Sie uns bereitgestellt haben, in einem strukturierten, gängigen und maschinenlesbaren Format zu erhalten. Sie haben außerdem das Recht, diese Daten einem anderen Verantwortlichen zu übermitteln.
                    </Typography>

                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                        7.6 Widerspruchsrecht
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Sie haben das Recht, aus Gründen, die sich aus Ihrer besonderen Situation ergeben, jederzeit gegen die Verarbeitung Sie betreffender personenbezogener Daten, die aufgrund von Art. 6 Abs. 1 lit. e oder f DSGVO erfolgt, Widerspruch einzulegen.
                    </Typography>

                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                        7.7 Widerruf einer Einwilligung
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Sie haben das Recht, eine erteilte Einwilligung jederzeit zu widerrufen. Durch den Widerruf der Einwilligung wird die Rechtmäßigkeit der aufgrund der Einwilligung bis zum Widerruf erfolgten Verarbeitung nicht berührt.
                    </Typography>
                </Box>

                {/* 8. Datensicherheit */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
                        8. Datensicherheit
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Wir verwenden innerhalb des Website-Besuchs das verbreitete SSL-Verfahren (Secure Socket Layer) in Verbindung mit der jeweils höchsten Verschlüsselungsstufe, die von Ihrem Browser unterstützt wird. Ob eine einzelne Seite unseres Internetauftrittes verschlüsselt übertragen wird, erkennen Sie an der geschlossenen Darstellung des Schlüssel- beziehungsweise Schloss-Symbols in der Statusleiste Ihres Browsers.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Wir bedienen uns geeigneter technischer und organisatorischer Sicherheitsmaßnahmen, um Ihre Daten gegen zufällige oder vorsätzliche Manipulationen, teilweisen oder vollständigen Verlust, Zerstörung oder gegen den unbefugten Zugriff Dritter zu schützen. Unsere Sicherheitsmaßnahmen werden entsprechend der technologischen Entwicklung fortlaufend verbessert.
                    </Typography>
                </Box>

                {/* 9. Aktualität und Änderung */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
                        9. Aktualität und Änderung dieser Datenschutzerklärung
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Diese Datenschutzerklärung ist aktuell gültig und hat den Stand Oktober 2025.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Durch die Weiterentwicklung unserer Website und Angebote darüber oder aufgrund geänderter gesetzlicher beziehungsweise behördlicher Vorgaben kann es notwendig werden, diese Datenschutzerklärung zu ändern. Die jeweils aktuelle Datenschutzerklärung kann jederzeit auf der Website von Ihnen abgerufen und ausgedruckt werden.
                    </Typography>
                </Box>

                {/* 10. Kontakt */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
                        10. Kontakt
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Bei Fragen zur Erhebung, Verarbeitung oder Nutzung Ihrer personenbezogenen Daten, bei Auskünften, Berichtigung, Sperrung oder Löschung von Daten sowie Widerruf erteilter Einwilligungen wenden Sie sich bitte an:
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {t('pages.imprint.email')}
                    </Typography>
                </Box>
            </Container>
        </>
    );
};

export default PrivacyPolicyPage;

